import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Truck, CreditCard, Check, MapPin, ChevronDown } from 'lucide-react';
import Header from './components/Header22';
import Footer from './components/Footer22';

const Checkout22 = () => {
    const navigate = useNavigate();
    const [currentStep, setCurrentStep] = useState(1);
    const [formData, setFormData] = useState({
        fullName: '',
        email: '',
        phone: '',
        addressLine1: '',
        addressLine2: '',
        city: '',
        state: '',
        pincode: '',
        country: 'India',
        cardNumber: '',
        cardName: '',
        expiryDate: '',
        cvv: '',
        paymentMethod: 'card',
    });

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (currentStep < 3) {
            setCurrentStep(currentStep + 1);
        } else {
            navigate('/template22/orders');
        }
    };

    const steps = [
        { number: 1, title: 'Shipping', icon: Truck },
        { number: 2, title: 'Payment', icon: CreditCard },
        { number: 3, title: 'Review', icon: Check },
    ];

    const orderItems = [
        {
            name: 'Chanderi Silk Saree',
            price: 8999,
            quantity: 1,
            size: 'Free Size',
            image: 'https://images.pexels.com/photos/10679171/pexels-photo-10679171.jpeg?auto=compress&cs=tinysrgb&w=800',
        },
        {
            name: 'Banarasi Zari Lehenga',
            price: 15999,
            quantity: 1,
            size: 'M',
            image: 'https://images.pexels.com/photos/9419032/pexels-photo-9419032.jpeg?auto=compress&cs=tinysrgb&w=800',
        },
    ];

    const subtotal = orderItems.reduce((sum, item) => sum + item.price * item.quantity, 0);
    const shipping = 0;
    const total = subtotal + shipping;

    const inputStyle = {
        width: '100%',
        padding: '14px 16px',
        backgroundColor: '#FFFFFF',
        border: '1px solid #E8DDD0',
        borderRadius: '8px',
        fontSize: '14px',
        color: '#5C3D2E',
        outline: 'none',
        fontFamily: "'Source Sans 3', -apple-system, sans-serif",
        transition: 'border-color 0.3s ease',
    };

    const labelStyle = {
        display: 'block',
        fontSize: '13px',
        fontWeight: 600,
        color: '#5C3D2E',
        marginBottom: '6px',
        fontFamily: "'Source Sans 3', sans-serif",
    };

    return (
        <>
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Cormorant+Infant:ital,wght@0,300;0,400;0,500;0,600;0,700;1,400&family=Source+Sans+3:wght@300;400;500;600;700&display=swap');

                .maati-checkout-layout {
                    display: flex;
                    flex-direction: column;
                    gap: 32px;
                    max-width: 1200px;
                    margin: 0 auto;
                    padding: 120px 24px 80px;
                }
                @media (min-width: 1024px) {
                    .maati-checkout-layout {
                        flex-direction: row;
                        gap: 48px;
                    }
                }
                .maati-checkout-input:focus {
                    border-color: #C4572A !important;
                }
                .maati-checkout-grid {
                    display: grid;
                    grid-template-columns: 1fr;
                    gap: 16px;
                }
                @media (min-width: 640px) {
                    .maati-checkout-grid {
                        grid-template-columns: 1fr 1fr;
                    }
                }
            `}</style>

            <div style={{ minHeight: '100vh', backgroundColor: '#FAF6F1', fontFamily: "'Source Sans 3', -apple-system, sans-serif" }}>
                <Header />

                <div className="maati-checkout-layout">
                    {/* Main Form */}
                    <div style={{ flex: 1 }}>
                        {/* Step Indicator */}
                        <div style={{
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            marginBottom: '40px', gap: '0',
                        }}>
                            {steps.map((step, i) => (
                                <React.Fragment key={step.number}>
                                    <div
                                        style={{
                                            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px',
                                            cursor: step.number < currentStep ? 'pointer' : 'default',
                                        }}
                                        onClick={() => {
                                            if (step.number < currentStep) setCurrentStep(step.number);
                                        }}
                                    >
                                        <div style={{
                                            width: '44px', height: '44px', borderRadius: '50%',
                                            backgroundColor: currentStep >= step.number ? '#C4572A' : '#F3ECE4',
                                            color: currentStep >= step.number ? '#FFFFFF' : '#8B7355',
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            transition: 'all 0.3s ease',
                                        }}>
                                            {currentStep > step.number ? <Check size={18} /> : <step.icon size={18} />}
                                        </div>
                                        <span style={{
                                            fontSize: '12px', fontWeight: 600,
                                            color: currentStep >= step.number ? '#5C3D2E' : '#8B7355',
                                            letterSpacing: '0.5px', textTransform: 'uppercase',
                                        }}>
                                            {step.title}
                                        </span>
                                    </div>
                                    {i < steps.length - 1 && (
                                        <div style={{
                                            flex: 1, height: '2px', maxWidth: '120px',
                                            backgroundColor: currentStep > step.number ? '#C4572A' : '#E8DDD0',
                                            margin: '0 16px', marginBottom: '28px',
                                            transition: 'background-color 0.3s ease',
                                        }} />
                                    )}
                                </React.Fragment>
                            ))}
                        </div>

                        <form onSubmit={handleSubmit}>
                            {/* Step 1: Shipping */}
                            {currentStep === 1 && (
                                <div style={{
                                    backgroundColor: '#FFFFFF', borderRadius: '12px',
                                    border: '1px solid #E8DDD0', padding: '32px',
                                }}>
                                    <h2 style={{
                                        fontFamily: "'Cormorant Infant', Georgia, serif",
                                        fontSize: '28px', fontWeight: 600, color: '#5C3D2E',
                                        margin: '0 0 8px 0',
                                    }}>
                                        Shipping Address
                                    </h2>
                                    <p style={{ fontSize: '14px', color: '#8B7355', marginBottom: '28px' }}>
                                        Where should we deliver your handcrafted pieces?
                                    </p>

                                    <div className="maati-checkout-grid">
                                        <div>
                                            <label style={labelStyle}>Full Name *</label>
                                            <input
                                                type="text" name="fullName" required
                                                value={formData.fullName} onChange={handleInputChange}
                                                placeholder="Enter your full name"
                                                className="maati-checkout-input"
                                                style={inputStyle}
                                            />
                                        </div>
                                        <div>
                                            <label style={labelStyle}>Email *</label>
                                            <input
                                                type="email" name="email" required
                                                value={formData.email} onChange={handleInputChange}
                                                placeholder="your@email.com"
                                                className="maati-checkout-input"
                                                style={inputStyle}
                                            />
                                        </div>
                                        <div>
                                            <label style={labelStyle}>Phone *</label>
                                            <input
                                                type="tel" name="phone" required
                                                value={formData.phone} onChange={handleInputChange}
                                                placeholder="+91 98765 43210"
                                                className="maati-checkout-input"
                                                style={inputStyle}
                                            />
                                        </div>
                                        <div>
                                            <label style={labelStyle}>Country</label>
                                            <select
                                                name="country" value={formData.country}
                                                onChange={handleInputChange}
                                                className="maati-checkout-input"
                                                style={{ ...inputStyle, cursor: 'pointer' }}
                                            >
                                                <option value="India">India</option>
                                                <option value="USA">United States</option>
                                                <option value="UK">United Kingdom</option>
                                                <option value="UAE">UAE</option>
                                                <option value="Singapore">Singapore</option>
                                            </select>
                                        </div>
                                    </div>

                                    <div style={{ marginTop: '16px' }}>
                                        <label style={labelStyle}>Address Line 1 *</label>
                                        <input
                                            type="text" name="addressLine1" required
                                            value={formData.addressLine1} onChange={handleInputChange}
                                            placeholder="House/Flat number, Street name"
                                            className="maati-checkout-input"
                                            style={inputStyle}
                                        />
                                    </div>

                                    <div style={{ marginTop: '16px' }}>
                                        <label style={labelStyle}>Address Line 2</label>
                                        <input
                                            type="text" name="addressLine2"
                                            value={formData.addressLine2} onChange={handleInputChange}
                                            placeholder="Landmark, Area (optional)"
                                            className="maati-checkout-input"
                                            style={inputStyle}
                                        />
                                    </div>

                                    <div className="maati-checkout-grid" style={{ marginTop: '16px' }}>
                                        <div>
                                            <label style={labelStyle}>City *</label>
                                            <input
                                                type="text" name="city" required
                                                value={formData.city} onChange={handleInputChange}
                                                placeholder="City"
                                                className="maati-checkout-input"
                                                style={inputStyle}
                                            />
                                        </div>
                                        <div>
                                            <label style={labelStyle}>State *</label>
                                            <input
                                                type="text" name="state" required
                                                value={formData.state} onChange={handleInputChange}
                                                placeholder="State"
                                                className="maati-checkout-input"
                                                style={inputStyle}
                                            />
                                        </div>
                                        <div>
                                            <label style={labelStyle}>Pincode *</label>
                                            <input
                                                type="text" name="pincode" required
                                                value={formData.pincode} onChange={handleInputChange}
                                                placeholder="6-digit pincode"
                                                className="maati-checkout-input"
                                                style={inputStyle}
                                            />
                                        </div>
                                    </div>

                                    <button
                                        type="submit"
                                        style={{
                                            marginTop: '32px', width: '100%', padding: '16px',
                                            backgroundColor: '#C4572A', color: '#FFFFFF', border: 'none',
                                            borderRadius: '8px', fontSize: '15px', fontWeight: 600,
                                            letterSpacing: '1px', textTransform: 'uppercase',
                                            cursor: 'pointer', fontFamily: "'Source Sans 3', sans-serif",
                                            transition: 'all 0.3s ease',
                                        }}
                                        onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#D4683B'; }}
                                        onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#C4572A'; }}
                                    >
                                        Continue to Payment
                                    </button>
                                </div>
                            )}

                            {/* Step 2: Payment */}
                            {currentStep === 2 && (
                                <div style={{
                                    backgroundColor: '#FFFFFF', borderRadius: '12px',
                                    border: '1px solid #E8DDD0', padding: '32px',
                                }}>
                                    <h2 style={{
                                        fontFamily: "'Cormorant Infant', Georgia, serif",
                                        fontSize: '28px', fontWeight: 600, color: '#5C3D2E',
                                        margin: '0 0 8px 0',
                                    }}>
                                        Payment Method
                                    </h2>
                                    <p style={{ fontSize: '14px', color: '#8B7355', marginBottom: '28px' }}>
                                        Secure payment powered by Razorpay
                                    </p>

                                    {/* Payment Options */}
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '28px' }}>
                                        {[
                                            { value: 'card', label: 'Credit / Debit Card' },
                                            { value: 'upi', label: 'UPI' },
                                            { value: 'netbanking', label: 'Net Banking' },
                                            { value: 'cod', label: 'Cash on Delivery' },
                                        ].map(option => (
                                            <label
                                                key={option.value}
                                                style={{
                                                    display: 'flex', alignItems: 'center', gap: '12px',
                                                    padding: '16px', borderRadius: '8px', cursor: 'pointer',
                                                    border: formData.paymentMethod === option.value
                                                        ? '2px solid #C4572A' : '1px solid #E8DDD0',
                                                    backgroundColor: formData.paymentMethod === option.value
                                                        ? '#FFF8F5' : '#FFFFFF',
                                                    transition: 'all 0.2s ease',
                                                }}
                                            >
                                                <input
                                                    type="radio" name="paymentMethod"
                                                    value={option.value}
                                                    checked={formData.paymentMethod === option.value}
                                                    onChange={handleInputChange}
                                                    style={{ accentColor: '#C4572A' }}
                                                />
                                                <span style={{ fontSize: '14px', fontWeight: 500, color: '#5C3D2E' }}>
                                                    {option.label}
                                                </span>
                                            </label>
                                        ))}
                                    </div>

                                    {formData.paymentMethod === 'card' && (
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                                            <div>
                                                <label style={labelStyle}>Card Number *</label>
                                                <input
                                                    type="text" name="cardNumber"
                                                    value={formData.cardNumber} onChange={handleInputChange}
                                                    placeholder="1234 5678 9012 3456"
                                                    className="maati-checkout-input"
                                                    style={inputStyle}
                                                />
                                            </div>
                                            <div>
                                                <label style={labelStyle}>Cardholder Name *</label>
                                                <input
                                                    type="text" name="cardName"
                                                    value={formData.cardName} onChange={handleInputChange}
                                                    placeholder="Name on card"
                                                    className="maati-checkout-input"
                                                    style={inputStyle}
                                                />
                                            </div>
                                            <div className="maati-checkout-grid">
                                                <div>
                                                    <label style={labelStyle}>Expiry Date *</label>
                                                    <input
                                                        type="text" name="expiryDate"
                                                        value={formData.expiryDate} onChange={handleInputChange}
                                                        placeholder="MM/YY"
                                                        className="maati-checkout-input"
                                                        style={inputStyle}
                                                    />
                                                </div>
                                                <div>
                                                    <label style={labelStyle}>CVV *</label>
                                                    <input
                                                        type="text" name="cvv"
                                                        value={formData.cvv} onChange={handleInputChange}
                                                        placeholder="123"
                                                        className="maati-checkout-input"
                                                        style={inputStyle}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    <div style={{ display: 'flex', gap: '12px', marginTop: '32px' }}>
                                        <button
                                            type="button"
                                            onClick={() => setCurrentStep(1)}
                                            style={{
                                                padding: '16px 24px', backgroundColor: 'transparent',
                                                color: '#5C3D2E', border: '1px solid #E8DDD0', borderRadius: '8px',
                                                fontSize: '14px', fontWeight: 600, cursor: 'pointer',
                                                fontFamily: "'Source Sans 3', sans-serif",
                                            }}
                                        >
                                            Back
                                        </button>
                                        <button
                                            type="submit"
                                            style={{
                                                flex: 1, padding: '16px', backgroundColor: '#C4572A',
                                                color: '#FFFFFF', border: 'none', borderRadius: '8px',
                                                fontSize: '15px', fontWeight: 600, letterSpacing: '1px',
                                                textTransform: 'uppercase', cursor: 'pointer',
                                                fontFamily: "'Source Sans 3', sans-serif",
                                                transition: 'all 0.3s ease',
                                            }}
                                            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#D4683B'; }}
                                            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#C4572A'; }}
                                        >
                                            Review Order
                                        </button>
                                    </div>
                                </div>
                            )}

                            {/* Step 3: Review */}
                            {currentStep === 3 && (
                                <div style={{
                                    backgroundColor: '#FFFFFF', borderRadius: '12px',
                                    border: '1px solid #E8DDD0', padding: '32px',
                                }}>
                                    <h2 style={{
                                        fontFamily: "'Cormorant Infant', Georgia, serif",
                                        fontSize: '28px', fontWeight: 600, color: '#5C3D2E',
                                        margin: '0 0 8px 0',
                                    }}>
                                        Review Your Order
                                    </h2>
                                    <p style={{ fontSize: '14px', color: '#8B7355', marginBottom: '28px' }}>
                                        Please confirm your details before placing the order
                                    </p>

                                    {/* Shipping Summary */}
                                    <div style={{
                                        backgroundColor: '#F3ECE4', borderRadius: '8px',
                                        padding: '20px', marginBottom: '24px',
                                    }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                                            <h3 style={{ fontSize: '15px', fontWeight: 600, color: '#5C3D2E', margin: 0 }}>
                                                Shipping Address
                                            </h3>
                                            <button
                                                type="button"
                                                onClick={() => setCurrentStep(1)}
                                                style={{
                                                    background: 'none', border: 'none',
                                                    color: '#C4572A', fontSize: '13px', fontWeight: 500,
                                                    cursor: 'pointer', fontFamily: "'Source Sans 3', sans-serif",
                                                }}
                                            >
                                                Edit
                                            </button>
                                        </div>
                                        <p style={{ fontSize: '14px', color: '#5C3D2E', margin: '0 0 4px 0' }}>
                                            {formData.fullName || 'Name'}
                                        </p>
                                        <p style={{ fontSize: '13px', color: '#8B7355', margin: 0, lineHeight: 1.6 }}>
                                            {formData.addressLine1 || 'Address'}{formData.addressLine2 ? `, ${formData.addressLine2}` : ''}<br />
                                            {formData.city || 'City'}, {formData.state || 'State'} {formData.pincode || 'Pincode'}<br />
                                            {formData.phone || 'Phone'} | {formData.email || 'Email'}
                                        </p>
                                    </div>

                                    {/* Payment Summary */}
                                    <div style={{
                                        backgroundColor: '#F3ECE4', borderRadius: '8px',
                                        padding: '20px', marginBottom: '24px',
                                    }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <h3 style={{ fontSize: '15px', fontWeight: 600, color: '#5C3D2E', margin: 0 }}>
                                                Payment
                                            </h3>
                                            <button
                                                type="button"
                                                onClick={() => setCurrentStep(2)}
                                                style={{
                                                    background: 'none', border: 'none',
                                                    color: '#C4572A', fontSize: '13px', fontWeight: 500,
                                                    cursor: 'pointer', fontFamily: "'Source Sans 3', sans-serif",
                                                }}
                                            >
                                                Edit
                                            </button>
                                        </div>
                                        <p style={{ fontSize: '14px', color: '#8B7355', marginTop: '8px', marginBottom: 0 }}>
                                            {formData.paymentMethod === 'card' ? `Card ending in ${formData.cardNumber.slice(-4) || '****'}` :
                                             formData.paymentMethod === 'upi' ? 'UPI Payment' :
                                             formData.paymentMethod === 'netbanking' ? 'Net Banking' : 'Cash on Delivery'}
                                        </p>
                                    </div>

                                    {/* Order Items */}
                                    <div style={{ marginBottom: '24px' }}>
                                        <h3 style={{ fontSize: '15px', fontWeight: 600, color: '#5C3D2E', marginBottom: '16px' }}>
                                            Order Items
                                        </h3>
                                        {orderItems.map((item, i) => (
                                            <div key={i} style={{
                                                display: 'flex', gap: '14px', padding: '14px 0',
                                                borderBottom: i < orderItems.length - 1 ? '1px solid #E8DDD0' : 'none',
                                            }}>
                                                <div style={{
                                                    width: '60px', height: '75px', borderRadius: '6px',
                                                    overflow: 'hidden', flexShrink: 0,
                                                }}>
                                                    <img src={item.image} alt={item.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                                </div>
                                                <div style={{ flex: 1 }}>
                                                    <p style={{ fontSize: '14px', fontWeight: 500, color: '#5C3D2E', margin: '0 0 4px 0' }}>{item.name}</p>
                                                    <p style={{ fontSize: '12px', color: '#8B7355', margin: 0 }}>Size: {item.size} | Qty: {item.quantity}</p>
                                                </div>
                                                <p style={{ fontSize: '15px', fontWeight: 600, color: '#C4572A', margin: 0 }}>
                                                    {'\u20B9'}{item.price.toLocaleString('en-IN')}
                                                </p>
                                            </div>
                                        ))}
                                    </div>

                                    <div style={{ display: 'flex', gap: '12px' }}>
                                        <button
                                            type="button"
                                            onClick={() => setCurrentStep(2)}
                                            style={{
                                                padding: '16px 24px', backgroundColor: 'transparent',
                                                color: '#5C3D2E', border: '1px solid #E8DDD0', borderRadius: '8px',
                                                fontSize: '14px', fontWeight: 600, cursor: 'pointer',
                                                fontFamily: "'Source Sans 3', sans-serif",
                                            }}
                                        >
                                            Back
                                        </button>
                                        <button
                                            type="submit"
                                            style={{
                                                flex: 1, padding: '16px', backgroundColor: '#C4572A',
                                                color: '#FFFFFF', border: 'none', borderRadius: '8px',
                                                fontSize: '15px', fontWeight: 600, letterSpacing: '1px',
                                                textTransform: 'uppercase', cursor: 'pointer',
                                                fontFamily: "'Source Sans 3', sans-serif",
                                                transition: 'all 0.3s ease',
                                            }}
                                            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#D4683B'; }}
                                            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#C4572A'; }}
                                        >
                                            Place Order
                                        </button>
                                    </div>
                                </div>
                            )}
                        </form>
                    </div>

                    {/* Order Summary Sidebar */}
                    <div style={{ width: '100%', maxWidth: '380px' }}>
                        <div style={{
                            backgroundColor: '#FFFFFF', borderRadius: '12px',
                            border: '1px solid #E8DDD0', padding: '28px',
                            position: 'sticky', top: '100px',
                        }}>
                            <h3 style={{
                                fontFamily: "'Cormorant Infant', Georgia, serif",
                                fontSize: '22px', fontWeight: 600, color: '#5C3D2E',
                                margin: '0 0 20px 0',
                            }}>
                                Order Summary
                            </h3>

                            {orderItems.map((item, i) => (
                                <div key={i} style={{
                                    display: 'flex', gap: '12px', marginBottom: '16px',
                                    paddingBottom: i < orderItems.length - 1 ? '16px' : '0',
                                    borderBottom: i < orderItems.length - 1 ? '1px solid #E8DDD0' : 'none',
                                }}>
                                    <div style={{
                                        width: '56px', height: '70px', borderRadius: '6px',
                                        overflow: 'hidden', flexShrink: 0,
                                    }}>
                                        <img src={item.image} alt={item.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                    </div>
                                    <div style={{ flex: 1 }}>
                                        <p style={{ fontSize: '13px', fontWeight: 500, color: '#5C3D2E', margin: '0 0 4px 0' }}>{item.name}</p>
                                        <p style={{ fontSize: '12px', color: '#8B7355', margin: 0 }}>Qty: {item.quantity}</p>
                                    </div>
                                    <p style={{ fontSize: '14px', fontWeight: 600, color: '#5C3D2E', margin: 0 }}>
                                        {'\u20B9'}{item.price.toLocaleString('en-IN')}
                                    </p>
                                </div>
                            ))}

                            <div style={{ borderTop: '1px solid #E8DDD0', paddingTop: '16px', marginTop: '8px' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px' }}>
                                    <span style={{ fontSize: '14px', color: '#8B7355' }}>Subtotal</span>
                                    <span style={{ fontSize: '14px', color: '#5C3D2E', fontWeight: 500 }}>{'\u20B9'}{subtotal.toLocaleString('en-IN')}</span>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px' }}>
                                    <span style={{ fontSize: '14px', color: '#8B7355' }}>Shipping</span>
                                    <span style={{ fontSize: '14px', color: '#2E7D32', fontWeight: 500 }}>Free</span>
                                </div>
                                <div style={{
                                    display: 'flex', justifyContent: 'space-between',
                                    borderTop: '1px solid #E8DDD0', paddingTop: '14px', marginTop: '8px',
                                }}>
                                    <span style={{ fontSize: '16px', fontWeight: 600, color: '#5C3D2E' }}>Total</span>
                                    <span style={{
                                        fontSize: '22px', fontWeight: 700, color: '#C4572A',
                                        fontFamily: "'Cormorant Infant', serif",
                                    }}>
                                        {'\u20B9'}{total.toLocaleString('en-IN')}
                                    </span>
                                </div>
                            </div>

                            <div style={{
                                marginTop: '20px', padding: '14px', backgroundColor: '#F3ECE4',
                                borderRadius: '8px', textAlign: 'center',
                            }}>
                                <p style={{ fontSize: '12px', color: '#5C3D2E', margin: 0, lineHeight: 1.5 }}>
                                    Your handcrafted pieces will be carefully inspected and wrapped with care before dispatch.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                <Footer />
            </div>
        </>
    );
};

export default Checkout22;
